import React, { useMemo, useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, Legend, ReferenceLine } from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { TrendingUp, Search, X, ToggleLeft, ToggleRight, Layers, Building2 } from 'lucide-react';
import axios from 'axios';
import { PERSONA_COLORS } from '../../utils/personaColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RevenueWorkloadChart = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewMode, setViewMode] = useState('groups'); // Default to 'groups'
  const [groupsData, setGroupsData] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch groups data on mount (since groups is default)
  useEffect(() => {
    setLoadingGroups(true);
    axios.get(`${API}/plan-groups`)
      .then(res => {
        setGroupsData(res.data);
        setLoadingGroups(false);
      })
      .catch(err => {
        console.error('Failed to fetch groups:', err);
        setLoadingGroups(false);
      });
  }, []);

  // Clear selection when switching view modes
  useEffect(() => {
    setSelectedClient(null);
    setSearchTerm('');
  }, [viewMode]);

  // Prepare scatter data for individual plans
  const planScatterData = useMemo(() => {
    const byPersona = {};
    
    data.forEach(client => {
      const persona = client.persona || 'Unknown';
      if (!byPersona[persona]) {
        byPersona[persona] = [];
      }
      
      byPersona[persona].push({
        x: client.workloadCost || 0,
        y: client.revenue || 0,
        name: client.planName || client.id,
        id: client.id,
        manager: client.manager,
        margin: client.margin || 0,
        messageCost: client.messageCost || 0,
        handleCost: client.handleCost || 0,
        meetingCost: client.meetingCost || 0,
        persona,
        type: 'plan'
      });
    });
    
    return byPersona;
  }, [data]);

  // Prepare scatter data for OC groups
  const groupScatterData = useMemo(() => {
    if (!groupsData?.groups) return {};
    
    const byPersona = {};
    
    groupsData.groups.forEach(group => {
      const persona = group.dominantPersona || 'Unknown';
      if (!byPersona[persona]) {
        byPersona[persona] = [];
      }
      
      byPersona[persona].push({
        x: group.totalWorkload || 0,
        y: group.totalRevenue || 0,
        name: group.basePlan,
        id: group.basePlan,
        manager: group.primaryManager,
        managers: group.managers,
        margin: group.margin / 100 || 0,
        planCount: group.planCount,
        totalMessageCost: group.totalMessageCost,
        totalHandleCost: group.totalHandleCost,
        totalMeetingCost: group.totalMeetingCost,
        profit: group.profit,
        isProfitable: group.isProfitable,
        persona,
        type: 'group'
      });
    });
    
    return byPersona;
  }, [groupsData]);

  // Get active scatter data based on view mode
  const activeScatterData = viewMode === 'plans' ? planScatterData : groupScatterData;

  // Search results
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    const results = [];
    
    Object.values(activeScatterData).flat().forEach(item => {
      const matchesName = item.name?.toLowerCase().includes(term);
      const matchesId = item.id?.toLowerCase().includes(term);
      const matchesManager = item.manager?.toLowerCase().includes(term);
      const matchesManagers = item.managers?.some(m => m.toLowerCase().includes(term));
      
      if (matchesName || matchesId || matchesManager || matchesManagers) {
        results.push(item);
      }
    });
    
    return results.slice(0, 10);
  }, [searchTerm, activeScatterData]);

  // Highlighted item data
  const highlightedData = useMemo(() => {
    if (!selectedClient) return [];
    return [selectedClient];
  }, [selectedClient]);

  // Calculate statistics
  const stats = useMemo(() => {
    const items = Object.values(activeScatterData).flat();
    const totalRevenue = items.reduce((sum, c) => sum + (c.y || 0), 0);
    const totalWorkload = items.reduce((sum, c) => sum + (c.x || 0), 0);
    const totalProfit = totalRevenue - totalWorkload;
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    
    const profitable = items.filter(c => c.y > c.x).length;
    const unprofitable = items.length - profitable;
    
    let correlation = 0;
    if (items.length > 2) {
      const meanX = totalWorkload / items.length;
      const meanY = totalRevenue / items.length;
      let numerator = 0;
      let denomX = 0;
      let denomY = 0;
      
      items.forEach(c => {
        const dx = (c.x || 0) - meanX;
        const dy = (c.y || 0) - meanY;
        numerator += dx * dy;
        denomX += dx * dx;
        denomY += dy * dy;
      });
      
      const denom = Math.sqrt(denomX * denomY);
      correlation = denom > 0 ? numerator / denom : 0;
    }
    
    const maxWorkload = Math.max(...items.map(c => c.x || 0), 0);
    const maxRevenue = Math.max(...items.map(c => c.y || 0), 0);
    
    return { 
      totalRevenue, totalWorkload, totalProfit, avgMargin, correlation, 
      profitable, unprofitable, maxWorkload, maxRevenue, count: items.length 
    };
  }, [activeScatterData]);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchTerm('');
  };

  const clearSelection = () => {
    setSelectedClient(null);
    setSearchTerm('');
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      const isProfit = d.y > d.x;
      
      if (d.type === 'group') {
        return (
          <div className={`p-4 rounded-xl border shadow-xl min-w-[240px] ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-blue-500" />
              <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Group {d.name}</p>
              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${d.isProfitable ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                {d.planCount} plans
              </span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Manager:</span>
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.manager}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Total Revenue:</span>
                <span className="text-emerald-500">${d.y.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Total Workload:</span>
                <span className="text-red-500">${d.x.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className={`flex justify-between gap-4 pt-1 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Gross Profit:</span>
                <span className={isProfit ? 'text-emerald-500' : 'text-red-500'}>
                  {isProfit ? '+' : ''}${d.profit?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Gross Margin:</span>
                <span className={d.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                  {(d.margin * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Persona:</span>
                <span style={{ color: PERSONA_COLORS[d.persona] }}>{d.persona}</span>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div className={`p-4 rounded-xl border shadow-xl min-w-[220px] ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Plan {d.name}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Manager:</span>
              <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.manager}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Revenue:</span>
              <span className="text-emerald-500">${d.y.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Workload Cost:</span>
              <span className="text-red-500">${d.x.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <div className={`flex justify-between gap-4 pt-1 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Gross Profit:</span>
              <span className={isProfit ? 'text-emerald-500' : 'text-red-500'}>
                {isProfit ? '+' : ''}${(d.y - d.x).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Gross Margin:</span>
              <span className={d.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                {(d.margin * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Persona:</span>
              <span style={{ color: PERSONA_COLORS[d.persona] }}>{d.persona}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const maxValue = Math.max(stats.maxWorkload, stats.maxRevenue) * 1.1;

  return (
    <div className={`rounded-2xl border p-6 transition-colors duration-300
      ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Revenue vs Workload Cost
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {viewMode === 'plans' 
              ? 'Each dot represents a plan. Above the line = profitable.'
              : 'Each dot represents an OC group. Above the line = profitable.'}
          </p>
        </div>
        
        <div className="flex gap-3 items-center">
          {/* View Mode Toggle */}
          <div className={`flex items-center rounded-lg border p-1 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
            <button
              onClick={() => setViewMode('plans')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                ${viewMode === 'plans' 
                  ? 'bg-emerald-500 text-white' 
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Plans
            </button>
            <button
              onClick={() => setViewMode('groups')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                ${viewMode === 'groups' 
                  ? 'bg-blue-500 text-white' 
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <Layers className="w-3.5 h-3.5" />
              OC Groups
            </button>
          </div>
          
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-500`}>
            {stats.profitable} profitable
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-500`}>
            {stats.unprofitable} unprofitable
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder={viewMode === 'plans' 
                ? "Search by plan number, name, or manager..." 
                : "Search by group number or manager..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm transition-colors
                ${isDark 
                  ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-emerald-500/50' 
                  : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-emerald-500'
                } focus:outline-none focus:ring-1 focus:ring-emerald-500/20`}
            />
          </div>
          {selectedClient && (
            <button
              onClick={clearSelection}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2
                ${isDark 
                  ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10' 
                  : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'}`}
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && searchTerm && (
          <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-lg max-h-64 overflow-y-auto
            ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
            {searchResults.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelectClient(item)}
                className={`w-full px-4 py-3 text-left text-sm transition-colors flex justify-between items-center
                  ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
              >
                <div className="flex items-center gap-2">
                  {item.type === 'group' && <Layers className="w-3.5 h-3.5 text-blue-500" />}
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {item.name}
                  </span>
                  {item.type === 'group' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500`}>
                      {item.planCount} plans
                    </span>
                  )}
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {item.manager}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-emerald-500 text-xs">${(item.y/1000).toFixed(1)}k rev</span>
                  <span className={`text-xs ${item.y > item.x ? 'text-emerald-500' : 'text-red-500'}`}>
                    {item.y > item.x ? 'Profitable' : 'Loss'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Item Info */}
      {selectedClient && (
        <div className={`mb-4 p-4 rounded-xl border ${
          viewMode === 'groups'
            ? isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'
            : isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedClient.type === 'group' && <Layers className="w-4 h-4 text-blue-500" />}
              <span className={`text-sm font-bold ${
                viewMode === 'groups'
                  ? isDark ? 'text-blue-400' : 'text-blue-700'
                  : isDark ? 'text-emerald-400' : 'text-emerald-700'
              }`}>
                {selectedClient.type === 'group' ? 'Group' : 'Plan'}: {selectedClient.name}
              </span>
              {selectedClient.type === 'group' && (
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-500`}>
                  {selectedClient.planCount} plans
                </span>
              )}
              <span className={`text-xs ${
                viewMode === 'groups'
                  ? isDark ? 'text-blue-500/70' : 'text-blue-600'
                  : isDark ? 'text-emerald-500/70' : 'text-emerald-600'
              }`}>
                ({selectedClient.manager})
              </span>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-emerald-500">Revenue: ${selectedClient.y.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-red-500">Workload: ${selectedClient.x.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className={selectedClient.y > selectedClient.x ? 'text-emerald-500' : 'text-red-500'}>
                Profit: ${(selectedClient.y - selectedClient.x).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator for groups */}
      {viewMode === 'groups' && loadingGroups && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          <span className={`ml-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading groups...</span>
        </div>
      )}

      {/* Chart */}
      {(!loadingGroups || viewMode === 'plans') && (
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="Workload Cost"
                domain={[0, maxValue]}
                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                label={{ value: 'Workload Cost ($)', position: 'bottom', offset: 20, fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }}
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="Revenue"
                domain={[0, maxValue]}
                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', offset: -10, fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }}
              />
              <ZAxis range={viewMode === 'groups' ? [60, 200] : [40, 150]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36}
                formatter={(value) => <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>{value}</span>}
              />
              
              <ReferenceLine 
                segment={[{ x: 0, y: 0 }, { x: maxValue, y: maxValue }]}
                stroke={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} 
                strokeDasharray="5 5"
                label={{ value: 'Breakeven', position: 'insideTopRight', fill: isDark ? '#64748b' : '#94a3b8', fontSize: 10 }}
              />
              
              {Object.entries(activeScatterData).map(([persona, points]) => (
                <Scatter 
                  key={persona}
                  name={persona} 
                  data={points} 
                  fill={PERSONA_COLORS[persona]}
                  fillOpacity={selectedClient ? 0.3 : 0.7}
                />
              ))}
              
              {/* Highlighted item */}
              {highlightedData.length > 0 && (
                <Scatter 
                  name="Selected"
                  data={highlightedData}
                  fill="#ffffff"
                  stroke={viewMode === 'groups' ? '#3b82f6' : '#10b981'}
                  strokeWidth={3}
                  shape={(props) => {
                    const { cx, cy } = props;
                    const color = viewMode === 'groups' ? '#3b82f6' : '#10b981';
                    return (
                      <g>
                        <circle cx={cx} cy={cy} r={14} fill={color} fillOpacity={0.3} />
                        <circle cx={cx} cy={cy} r={10} fill={color} stroke="#fff" strokeWidth={2} />
                        <circle cx={cx} cy={cy} r={22} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 4">
                          <animate attributeName="r" from="16" to="28" dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="1" to="0" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                      </g>
                    );
                  }}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Summary stats */}
      <div className={`mt-4 pt-4 border-t grid grid-cols-4 gap-4 ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {viewMode === 'plans' ? 'Total Plans' : 'Total Groups'}
          </p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.count}</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Revenue</p>
          <p className="text-lg font-bold text-emerald-500">${(stats.totalRevenue/1000000).toFixed(2)}M</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Workload</p>
          <p className="text-lg font-bold text-red-500">${(stats.totalWorkload/1000000).toFixed(2)}M</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Margin</p>
          <p className={`text-lg font-bold ${stats.avgMargin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {stats.avgMargin >= 0 ? '+' : ''}{stats.avgMargin.toFixed(1)}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default RevenueWorkloadChart;
